import { useState } from 'react';
import { Chip, ListItemIcon, ListItemText, ListSubheader, MenuItem, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useNotify, useRefresh } from 'react-admin';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { useFeatureFlags } from './FeatureGate';
import { saveOwnVersion, VERSION_LABELS, type FeatureVersion } from './featureFlagsApi';

/**
 * Wybór wersji w menu użytkownika i znacznik wersji w pasku (decyzja
 * użytkownika 12.10.2026, F2: „dobrze by było, gdyby można było pokazać
 * nową i starą wersję i wybrać ją w menu wyboru wersji”).
 *
 * Tylko na środowisku, które na to pozwala (`PRZELACZNIK_WERSJI` w API —
 * staging); na produkcji obu elementów nie ma. Wybór jest pamiętany na
 * koncie: „obecna” wyłącza dla tego konta wszystkie przełączniki funkcji
 * do decyzji, „nowa” pokazuje stan przełączników środowiska.
 */

const ORDER: FeatureVersion[] = ['current', 'new'];

/** Pozycje „Wersja: obecna / nowa” w menu użytkownika. */
export const VersionMenuItems = () => {
  const state = useFeatureFlags();
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);

  if (!state?.versionSwitchAvailable) return null;

  const choose = async (version: FeatureVersion) => {
    if (version === state.version || saving) return;
    setSaving(true);
    try {
      await saveOwnVersion(version);
      notify(`Wersja aplikacji: ${VERSION_LABELS[version]}`, { type: 'success' });
      // Dane ekranu pobrane w poprzedniej wersji — od nowa.
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ListSubheader sx={{ lineHeight: '32px' }}>Wersja aplikacji</ListSubheader>
      {ORDER.map((v) => (
        <MenuItem
          key={v}
          selected={state.version === v}
          disabled={saving}
          onClick={() => void choose(v)}
          aria-checked={state.version === v}
          role="menuitemradio"
        >
          <ListItemIcon>{state.version === v ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>Wersja: {VERSION_LABELS[v]}</ListItemText>
        </MenuItem>
      ))}
    </>
  );
};

/** Opis znacznika w dymku — co znaczy wersja. */
export const VERSION_HINTS: Record<FeatureVersion, string> = {
  current:
    'Funkcje czekające na decyzję są wyłączone — aplikacja działa jak na produkcji. Zmiana w menu użytkownika.',
  new: 'Widzisz funkcje czekające na decyzję (według przełączników tego środowiska). Zmiana w menu użytkownika.',
};

/** Znacznik aktualnej wersji w pasku — tylko na środowisku z wyborem wersji. */
export const VersionBadge = () => {
  const state = useFeatureFlags();
  if (!state?.versionSwitchAvailable || state.version === null) return null;
  const v = state.version;
  return (
    <Tooltip title={VERSION_HINTS[v]}>
      <Chip
        size="small"
        data-testid="version-badge"
        label={`Wersja: ${VERSION_LABELS[v]}`}
        color={v === 'new' ? 'warning' : 'default'}
        sx={{ mr: 1, fontWeight: 600, ...(v === 'current' ? { bgcolor: 'background.paper' } : {}) }}
      />
    </Tooltip>
  );
};
